import {
    GALLERY_COLUMN_WIDTH_MOBILE,
    GALLERY_COLUMN_WIDTH,
} from '@modules/page-builder/config/column-constants';
import type { ImageProps } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import type { ImageColumnProps } from '@modules/page-builder/types';
import DetailSingleImageShell from '../detail-single-image-shell/DetailSingleImageShell';
import * as S from './GalleryModule.style';
import React from 'react';
import PreviewImage from '@modules/page-builder/component/edit/page-builder-image/PreviewImage';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { Image } from '@rocco/components/image';
import { ImageModuleExtraSpacing } from '@modules/page-builder/component/detail-mobile/detail-modules/DetailModules.style';
import ProductInfo from '@modules/page-builder/component/common/product-info/ProductInfo';
import LazyImageModule from '../detail-modules/LazyImageModule';

/** Number of modules to render eagerly (above-fold) */
const EAGER_COUNT = 2;

interface GalleryModuleProps {
    columns: ImageColumnProps[];
    /** Index of this module for eager loading determination */
    moduleIndex?: number;
}

const GalleryModule = ({ columns, moduleIndex = 0 }: GalleryModuleProps) => {
    const { css } = useStyles();
    const isEager = moduleIndex < EAGER_COUNT;

    return (
        <div className={css(S.GalleryModuleContainer, ImageModuleExtraSpacing)}>
            {columns.map((column) => {
                const imageProps: Omit<ImageProps, 'src'> = {
                    layout: 'fill',
                    width: column.dimension.width,
                    height: column.dimension.height,
                    objectFit: 'cover',
                    fadeIn: false,
                    alt: '',
                };

                const imageShell = column.imageUrl ? (
                    <LazyImageModule
                        key={column.id}
                        className={css(S.ImageContainer)}
                        dimension={column.dimension}
                        moduleIndex={moduleIndex}
                        eagerCount={EAGER_COUNT}
                        skipContentVisibility
                    >
                        <DetailSingleImageShell
                            dimension={column.dimension}
                            libraryImage={column.libraryImage}
                            taggingEnabled={false}
                            imageURL={column.imageUrl}
                            isProduct={!!column.productId}
                        >
                            <Image
                                src={column.imageUrl}
                                width={column.dimension.width ?? undefined}
                                height={column.dimension.height ?? undefined}
                                fill
                                fit="cover"
                                loading={isEager ? 'eager' : 'lazy'}
                                fetchPriority={isEager ? 'high' : 'low'}
                                sizes={getImagesSizes({
                                    desktop: GALLERY_COLUMN_WIDTH,
                                    mobile: GALLERY_COLUMN_WIDTH_MOBILE,
                                })}
                                alt={
                                    column.caption ||
                                    column?.libraryImage?.Title ||
                                    ''
                                }
                                className={
                                    column.productId ? 'mix-blend-multiply' : ''
                                }
                            />
                        </DetailSingleImageShell>
                    </LazyImageModule>
                ) : (
                    <div className={css(S.ImageContainer)} key={column.id}>
                        <PreviewImage
                            show={true}
                            imageUrl={column.imageOptimistic}
                            imageProps={imageProps}
                        />
                    </div>
                );

                return column.productId ? (
                    <ProductInfo
                        key={column.id}
                        productId={column.productId}
                        variant={'01'}
                        image={imageShell}
                        clickable={true}
                        dimension={column.dimension}
                    />
                ) : (
                    imageShell
                );
            })}
        </div>
    );
};

export default GalleryModule;
